// Cột bảng danh sách đợt thưởng. Tách khỏi reward-program-list.js để file
// container chỉ còn state/lọc (giữ dưới 200 dòng).
import React from "react";
import { Button, Space, Tag, Tooltip, Progress, Badge } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faCopy, faChartLine } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { rewardImageUrl } from "./reward-image-url";
import { PROGRAM_STATUS_META, CAMP_LABELS, fmtMoney, fmtNumber, fmtUnix } from "./reward-shared-meta";

/** Màu thanh ngân sách theo mức đã tiêu — gần cạn phải nhìn là thấy. */
const budgetStroke = (pct) => (pct >= 90 ? "#dc2626" : pct >= 70 ? "#ea580c" : "#16a34a");

/**
 * Dòng phụ dưới khoảng thời gian: đợt chưa tới ngày thì đếm ngược tới lúc mở,
 * đang chạy thì đếm ngược tới lúc đóng. Admin hay hỏi "còn mấy ngày nữa".
 */
const timeHint = (record) => {
    const now = dayjs();
    if (record.startTime && now.isBefore(dayjs.unix(Number(record.startTime)))) {
        return { text: `Mở sau ${dayjs.unix(Number(record.startTime)).diff(now, "day")} ngày`, cls: "text-blue-600" };
    }
    if (record.endTime) {
        const end = dayjs.unix(Number(record.endTime));
        if (now.isBefore(end)) {
            const days = end.diff(now, "day");
            return { text: days > 0 ? `Còn ${days} ngày` : `Còn ${end.diff(now, "hour")} giờ`, cls: "text-green-600" };
        }
        return { text: "Đã đóng", cls: "text-content-3" };
    }
    return null;
};

export const buildRewardProgramColumns = ({ typeLabels, onOpen, onClone, onEdit, onDelete }) => [
    {
        title: "Chương trình", dataIndex: "name", ellipsis: true,
        sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
        render: (v, r) => (
            <div className="flex items-center gap-3">
                {/* Ảnh serve từ API origin — đường dẫn tương đối trên domain admin sẽ 404 */}
                {r.thumbnail ? (
                    <img src={rewardImageUrl(r.thumbnail)} alt="" className="w-10 h-10 rounded-lg object-cover border border-line shrink-0" />
                ) : (
                    <div className="w-10 h-10 rounded-lg bg-surface-muted border border-line flex items-center justify-center shrink-0">🎁</div>
                )}
                <div className="min-w-0">
                    <div className="font-medium truncate">
                        <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => onOpen(r)}>{v}</span>
                        {r.isHot ? <Tag color="red" className="ml-1">HOT</Tag> : null}
                    </div>
                    <div className="text-xs text-content-3 truncate">
                        #{r.id} · {r.slug}
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: "Loại", dataIndex: "type", width: 165,
        render: (v, r) => (
            <Space direction="vertical" size={2}>
                <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>{typeLabels[v] || v}</Tag>
                {r.campId ? <Tag style={{ marginInlineEnd: 0 }}>{CAMP_LABELS[r.campId] || r.campId}</Tag> : null}
            </Space>
        ),
    },
    {
        title: "Trạng thái", dataIndex: "status", width: 140,
        render: (v, r) => {
            const meta = PROGRAM_STATUS_META[v] || { color: "default", label: v };
            return (
                <Space direction="vertical" size={2}>
                    <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>
                    <Badge status={r.enabled ? "success" : "default"} text={<span className="text-xs text-content-3">{r.enabled ? "Đang bật" : "Đang tắt"}</span>} />
                </Space>
            );
        },
    },
    {
        title: "Thời gian", width: 185,
        sorter: (a, b) => Number(a.startTime || 0) - Number(b.startTime || 0),
        defaultSortOrder: "descend",
        render: (_, r) => {
            const hint = timeHint(r);
            return (
                <div className="text-xs leading-5">
                    <div className="text-content-2">{fmtUnix(r.startTime)}</div>
                    <div className="text-content-2">→ {fmtUnix(r.endTime)}</div>
                    {hint ? <div className={hint.cls}>{hint.text}</div> : null}
                </div>
            );
        },
    },
    {
        title: "Ngân sách", width: 200,
        sorter: (a, b) => Number(a.usedBudget || 0) - Number(b.usedBudget || 0),
        render: (_, r) => {
            if (r.totalBudget == null) {
                return (
                    <div className="text-xs">
                        <div className="text-content-3">Không giới hạn</div>
                        <div className="text-content-2">Đã dùng {fmtMoney(r.usedBudget)}</div>
                    </div>
                );
            }
            const pct = Math.min(100, Math.round((Number(r.usedBudget) / Number(r.totalBudget)) * 100));
            return (
                <Tooltip title={`Đã dùng ${fmtMoney(r.usedBudget)} / ${fmtMoney(r.totalBudget)} (chưa gồm hoa hồng giới thiệu ~5-10% ngoài ngân sách)`}>
                    <Progress percent={pct} size="small" strokeColor={budgetStroke(pct)} />
                    <span className="text-xs text-content-3">{fmtMoney(r.usedBudget)} / {fmtMoney(r.totalBudget)}</span>
                </Tooltip>
            );
        },
    },
    {
        title: "Tham gia", dataIndex: "participantsCount", width: 95, align: "center",
        sorter: (a, b) => Number(a.participantsCount || 0) - Number(b.participantsCount || 0),
        render: (v, r) => (
            <Tooltip title="Xem danh sách người đăng ký">
                <span className="text-blue-600 cursor-pointer font-medium" onClick={() => onOpen(r)}>{fmtNumber(v)}</span>
            </Tooltip>
        ),
    },
    {
        title: "Hành động", key: "action", width: 175, fixed: "right",
        render: (_, record) => (
            <Space size={4}>
                <Tooltip title="Chi tiết: số liệu · người đăng ký · phân bổ đơn">
                    <Button size="small" type="primary" ghost icon={<FontAwesomeIcon icon={faChartLine} />} onClick={() => onOpen(record)} />
                </Tooltip>
                <Tooltip title="Nhân bản đợt">
                    <Button size="small" icon={<FontAwesomeIcon icon={faCopy} />} onClick={() => onClone(record)} />
                </Tooltip>
                <Tooltip title="Sửa đợt">
                    <Button size="small" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => onEdit(record)} />
                </Tooltip>
                <Tooltip title="Xoá đợt">
                    <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => onDelete(record)} />
                </Tooltip>
            </Space>
        ),
    },
];
